import {
  normalizeCatalogOptions,
  readTaskAreaIds,
  type AssigneeDoc,
  type ExecutionDoc,
  type ExecutionStatus,
  type ExecutionType,
  type ProjectDoc,
  type TaskDoc,
} from '@mct/shared';

export const UNASSIGNED = '__none__';

export interface TaskListRow {
  taskId: string;
  /**
   * Stable source key from the migration xlsx (the sheet's B-column
   * "No", e.g. `"TT-0001"`) — surfaced as the leftmost「No」column so
   * users can cross-reference rows against the original sheet. `null`
   * for hand-created tasks, which render an empty cell.
   */
  sourceTaskId: string | null;
  name: string;
  description: string;
  category: string;
  /**
   * Canonical link to `/projects/{pid}/assignees`. `null` when the task
   * is unassigned. Display name and color resolve through the assignee
   * doc (see `assignee`).
   */
  assigneeId: string | null;
  /**
   * Snapshot of the resolved assignee from the project's list. Used by
   * the chip renderer so the cell shows the same color and name as the
   * WBS / DAG views. `null` when `assigneeId` is unset or the
   * referenced doc has been deleted.
   */
  assignee: Pick<AssigneeDoc, 'name' | 'colorIndex'> | null;
  /**
   * UID of the project member who actually executed this task in the
   * current run, sourced from the matching `ExecutionDoc`. Independent
   * of `assigneeId` — assignees are project labels, executors are real
   * users. `null` when no execution exists yet or the executor wasn't
   * recorded.
   */
  actualAssigneeUserId: string | null;
  executionType: ExecutionType;
  status: ExecutionStatus;
  /**
   * Per-task planned start date for the current run, sourced from the
   * matching `ExecutionDoc.plannedStartDate` (an ISO date string, e.g.
   * `"2026-04-22"`). `null` when no execution exists yet or the field
   * is unset. Surfaced as the「予定開始日」column.
   */
  plannedStartDate: string | null;
  completedAt: Date | null;
  backlogCount: number;
  // Denormalised fields surfaced from the import so the list filter can
  // expose every task attribute users already populated (系統 / 領域 /
  // シナリオ / フェーズラベル). All optional because hand-created tasks
  // won't have them.
  scenario: string | null;
  system: string | null;
  area: string | null;
  /**
   * Canonical multi-value 領域 for this task: the raw `areaIds`
   * (`WbsCatalogOption.id`s) and their resolved display labels, looked up
   * through the project's `wbsCatalog.area`. `areaLabels` is what the
   * 領域 column renders (comma-joined); an entry with no catalog match
   * falls back to its raw id so the cell is never blank for a real value.
   */
  areaIds: string[];
  areaLabels: string[];
  /**
   * 移行元 / 移行先システム, sourced from `TaskDoc.sysFrom` / `sysTo`
   * (imported from the xlsx). `null` when unset. Surfaced as the dedicated
   * 「sys From」/「sys To」columns. `system` above keeps the legacy
   * single-value derivation used by the existing システム filter.
   */
  sysFrom: string | null;
  sysTo: string | null;
  applicableRunLabels: string[];
}

export interface TaskListFilter {
  categories: string[];
  assignees: string[];
  statuses: ExecutionStatus[];
  systems: string[];
  areas: string[];
  scenarios: string[];
  phaseLabels: string[];
  search: string;
}

export type SortKey =
  | 'sourceTaskId'
  | 'name'
  | 'category'
  | 'area'
  | 'sysFrom'
  | 'sysTo'
  | 'assignee'
  | 'status'
  | 'plannedStartDate'
  | 'completedAt';
export type SortDir = 'asc' | 'desc';

export interface SortSpec {
  key: SortKey;
  dir: SortDir;
}

export const EMPTY_FILTER: TaskListFilter = {
  categories: [],
  assignees: [],
  statuses: [],
  systems: [],
  areas: [],
  scenarios: [],
  phaseLabels: [],
  search: '',
};

export const DEFAULT_SORT: SortSpec = { key: 'sourceTaskId', dir: 'asc' };

const VALID_STATUSES: readonly ExecutionStatus[] = [
  'not_started',
  'in_progress',
  'blocked',
  'done',
  'skipped',
];

const VALID_SORT_KEYS: readonly SortKey[] = [
  'sourceTaskId',
  'name',
  'category',
  'area',
  'sysFrom',
  'sysTo',
  'assignee',
  'status',
  'plannedStartDate',
  'completedAt',
];

const STATUS_SORT: Record<ExecutionStatus, number> = {
  blocked: 0,
  in_progress: 1,
  not_started: 2,
  done: 3,
  skipped: 4,
};

export function joinTasksAndExecutions(
  tasks: readonly TaskDoc[],
  executions: readonly ExecutionDoc[],
  // `wbsCatalog` resolves a task's `areaIds` to display labels for the
  // 領域 column; `members` is reserved for future per-row member metadata.
  project: Pick<ProjectDoc, 'members' | 'wbsCatalog'> | null,
  backlogIssueKeys: ReadonlySet<string>,
  assignees: readonly AssigneeDoc[] = [],
): TaskListRow[] {
  const execByTaskId = new Map<string, ExecutionDoc>();
  for (const e of executions) execByTaskId.set(e.taskId, e);
  const assigneeById = new Map<string, AssigneeDoc>();
  for (const a of assignees) assigneeById.set(a.assigneeId, a);
  // id → label map for 領域, so the column can render human-readable names
  // instead of the stored `WbsCatalogOption.id`s. Falls back to the raw id
  // for any value the catalog doesn't know (legacy / hand-typed entries).
  const areaLabelById = new Map<string, string>();
  for (const opt of normalizeCatalogOptions(project?.wbsCatalog?.area)) {
    areaLabelById.set(opt.id, opt.label);
  }
  return tasks.map((t) => {
    const exec = execByTaskId.get(t.taskId);
    const imported = t as TaskDoc & {
      sourceScenario?: string | null;
      sysFrom?: string | null;
      sysTo?: string | null;
      area?: string | null;
      applicableRunLabels?: string[];
    };
    const assigneeId = t.assigneeId ?? null;
    const assigneeDoc = assigneeId ? assigneeById.get(assigneeId) ?? null : null;
    const completedAt = exec?.completedAt ? exec.completedAt.toDate() : null;
    const backlogCount = exec
      ? exec.backlogIssueKeys.filter((k) => backlogIssueKeys.has(k)).length
      : 0;
    const areaIds = readTaskAreaIds(t);
    const areaLabels = areaIds.map((id) => areaLabelById.get(id) ?? id);
    return {
      taskId: t.taskId,
      sourceTaskId: t.sourceTaskId ?? null,
      name: t.name,
      description: t.description,
      category: t.category,
      assigneeId,
      assignee: assigneeDoc
        ? { name: assigneeDoc.name, colorIndex: assigneeDoc.colorIndex }
        : null,
      actualAssigneeUserId: exec?.actualAssigneeUserId ?? null,
      executionType: t.executionType,
      status: exec?.status ?? 'not_started',
      plannedStartDate: exec?.plannedStartDate ?? null,
      completedAt,
      backlogCount,
      scenario: imported.sourceScenario ?? null,
      system: imported.sysFrom ?? imported.sysTo ?? null,
      // Prefer the canonical multi-value 領域 (primary entry) via the
      // dual-read helper; fall back to the legacy import-only `area` so
      // pre-PR docs still surface their value. This row model keeps a
      // single primary area, which is acceptable for the list view.
      area: areaIds[0] ?? imported.area ?? null,
      areaIds,
      areaLabels,
      sysFrom: imported.sysFrom ?? null,
      sysTo: imported.sysTo ?? null,
      applicableRunLabels: imported.applicableRunLabels ?? [],
    };
  });
}

export function sortRows(
  rows: readonly TaskListRow[],
  spec: SortSpec,
): TaskListRow[] {
  const { key, dir } = spec;
  const mul = dir === 'asc' ? 1 : -1;
  const cmp = (a: TaskListRow, b: TaskListRow): number => {
    switch (key) {
      case 'sourceTaskId': {
        // Hand-created tasks (no source row) sink to the bottom in both
        // directions, the same way null 完了日 does — they have no「No」
        // to order against. `numeric` so "TT-2" precedes "TT-10".
        const an = !a.sourceTaskId;
        const bn = !b.sourceTaskId;
        if (an && bn) return 0;
        if (an) return 1;
        if (bn) return -1;
        return (
          a.sourceTaskId!.localeCompare(b.sourceTaskId!, 'ja', {
            numeric: true,
          }) * mul
        );
      }
      case 'name':
        return a.name.localeCompare(b.name, 'ja') * mul;
      case 'category':
        return (a.category || '').localeCompare(b.category || '', 'ja') * mul;
      case 'area': {
        // Sort by the joined 領域 label (名前順). Tasks with no 領域 sink
        // to the bottom in both directions, matching the other
        // optional-value columns.
        const al = a.areaLabels.join('、');
        const bl = b.areaLabels.join('、');
        const an = al === '';
        const bn = bl === '';
        if (an && bn) return 0;
        if (an) return 1;
        if (bn) return -1;
        return al.localeCompare(bl, 'ja') * mul;
      }
      case 'sysFrom': {
        const an = !a.sysFrom;
        const bn = !b.sysFrom;
        if (an && bn) return 0;
        if (an) return 1;
        if (bn) return -1;
        return a.sysFrom!.localeCompare(b.sysFrom!, 'ja') * mul;
      }
      case 'sysTo': {
        const an = !a.sysTo;
        const bn = !b.sysTo;
        if (an && bn) return 0;
        if (an) return 1;
        if (bn) return -1;
        return a.sysTo!.localeCompare(b.sysTo!, 'ja') * mul;
      }
      case 'assignee':
        return (
          (a.assignee?.name ?? '').localeCompare(b.assignee?.name ?? '', 'ja') *
          mul
        );
      case 'status':
        return (STATUS_SORT[a.status] - STATUS_SORT[b.status]) * mul;
      case 'plannedStartDate': {
        // ISO date strings (YYYY-MM-DD) sort lexicographically === by
        // date. Unset dates sink to the bottom in both directions, the
        // same null-handling as 完了日.
        const an = !a.plannedStartDate;
        const bn = !b.plannedStartDate;
        if (an && bn) return 0;
        if (an) return 1;
        if (bn) return -1;
        return a.plannedStartDate!.localeCompare(b.plannedStartDate!) * mul;
      }
      case 'completedAt': {
        const an = a.completedAt == null;
        const bn = b.completedAt == null;
        if (an && bn) return 0;
        if (an) return 1;
        if (bn) return -1;
        return (a.completedAt!.getTime() - b.completedAt!.getTime()) * mul;
      }
    }
  };
  return [...rows].sort(cmp);
}

export function filterRows(
  rows: readonly TaskListRow[],
  filter: TaskListFilter,
): TaskListRow[] {
  const search = filter.search.trim().toLowerCase();
  return rows.filter((r) => {
    if (
      filter.categories.length > 0 &&
      !filter.categories.includes(r.category)
    ) {
      return false;
    }
    if (filter.assignees.length > 0) {
      const key = r.assigneeId ?? UNASSIGNED;
      if (!filter.assignees.includes(key)) return false;
    }
    if (filter.statuses.length > 0 && !filter.statuses.includes(r.status)) {
      return false;
    }
    if (filter.systems.length > 0 && (!r.system || !filter.systems.includes(r.system))) {
      return false;
    }
    if (filter.areas.length > 0 && (!r.area || !filter.areas.includes(r.area))) {
      return false;
    }
    if (filter.scenarios.length > 0 && (!r.scenario || !filter.scenarios.includes(r.scenario))) {
      return false;
    }
    if (filter.phaseLabels.length > 0) {
      const any = r.applicableRunLabels.some((l) => filter.phaseLabels.includes(l));
      if (!any) return false;
    }
    if (search) {
      const hay = `${r.name}\n${r.description}`.toLowerCase();
      if (!hay.includes(search)) return false;
    }
    return true;
  });
}

export interface TaskListSummary {
  total: number;
  done: number;
  inProgress: number;
  blocked: number;
  notStarted: number;
  skipped: number;
  /** `done / (total - skipped)`; `0` when denominator is zero. */
  progressRatio: number;
}

export function computeSummary(rows: readonly TaskListRow[]): TaskListSummary {
  let done = 0;
  let inProgress = 0;
  let blocked = 0;
  let notStarted = 0;
  let skipped = 0;
  for (const r of rows) {
    switch (r.status) {
      case 'done':
        done += 1;
        break;
      case 'in_progress':
        inProgress += 1;
        break;
      case 'blocked':
        blocked += 1;
        break;
      case 'not_started':
        notStarted += 1;
        break;
      case 'skipped':
        skipped += 1;
        break;
    }
  }
  const denom = rows.length - skipped;
  const progressRatio = denom > 0 ? done / denom : 0;
  return {
    total: rows.length,
    done,
    inProgress,
    blocked,
    notStarted,
    skipped,
    progressRatio,
  };
}

export function encodeFilterToParams(
  filter: TaskListFilter,
  sort: SortSpec,
  base?: URLSearchParams,
): URLSearchParams {
  const out = new URLSearchParams(base);
  for (const key of ['cat', 'assignee', 'status', 'sys', 'area', 'scn', 'phase', 'q', 'sort', 'dir']) {
    out.delete(key);
  }
  if (filter.categories.length) out.set('cat', filter.categories.join(','));
  if (filter.assignees.length) out.set('assignee', filter.assignees.join(','));
  if (filter.statuses.length) out.set('status', filter.statuses.join(','));
  if (filter.systems.length) out.set('sys', filter.systems.join(','));
  if (filter.areas.length) out.set('area', filter.areas.join(','));
  if (filter.scenarios.length) out.set('scn', filter.scenarios.join(','));
  if (filter.phaseLabels.length) out.set('phase', filter.phaseLabels.join(','));
  if (filter.search) out.set('q', filter.search);
  if (sort.key !== DEFAULT_SORT.key) out.set('sort', sort.key);
  if (sort.dir !== DEFAULT_SORT.dir) out.set('dir', sort.dir);
  return out;
}

export function decodeFilterFromParams(params: URLSearchParams): {
  filter: TaskListFilter;
  sort: SortSpec;
} {
  const statuses = (params.get('status')?.split(',') ?? [])
    .filter(Boolean)
    .filter((s): s is ExecutionStatus =>
      (VALID_STATUSES as readonly string[]).includes(s),
    );
  const sortKeyRaw = params.get('sort') ?? DEFAULT_SORT.key;
  const key = (VALID_SORT_KEYS as readonly string[]).includes(sortKeyRaw)
    ? (sortKeyRaw as SortKey)
    : DEFAULT_SORT.key;
  const dirRaw = params.get('dir');
  const dir: SortDir = dirRaw === 'desc' ? 'desc' : 'asc';
  return {
    filter: {
      categories: params.get('cat')?.split(',').filter(Boolean) ?? [],
      assignees: params.get('assignee')?.split(',').filter(Boolean) ?? [],
      statuses,
      systems: params.get('sys')?.split(',').filter(Boolean) ?? [],
      areas: params.get('area')?.split(',').filter(Boolean) ?? [],
      scenarios: params.get('scn')?.split(',').filter(Boolean) ?? [],
      phaseLabels: params.get('phase')?.split(',').filter(Boolean) ?? [],
      search: params.get('q') ?? '',
    },
    sort: { key, dir },
  };
}

export function collectCategories(rows: readonly TaskListRow[]): string[] {
  const set = new Set<string>();
  for (const r of rows) if (r.category) set.add(r.category);
  return [...set].sort((a, b) => a.localeCompare(b, 'ja'));
}

export function collectSystems(rows: readonly TaskListRow[]): string[] {
  const set = new Set<string>();
  for (const r of rows) if (r.system) set.add(r.system);
  return [...set].sort((a, b) => a.localeCompare(b, 'ja'));
}

export function collectAreas(rows: readonly TaskListRow[]): string[] {
  const set = new Set<string>();
  for (const r of rows) if (r.area) set.add(r.area);
  return [...set].sort((a, b) => a.localeCompare(b, 'ja'));
}

export function collectScenarios(rows: readonly TaskListRow[]): string[] {
  const set = new Set<string>();
  for (const r of rows) if (r.scenario) set.add(r.scenario);
  return [...set].sort((a, b) => a.localeCompare(b, 'ja'));
}

export function collectPhaseLabels(rows: readonly TaskListRow[]): string[] {
  const set = new Set<string>();
  for (const r of rows) for (const l of r.applicableRunLabels) set.add(l);
  return [...set].sort((a, b) => a.localeCompare(b, 'ja'));
}

/** One applied-filter condition, rendered as an inline chip next to the
 *  「フィルタ」toggle so the active conditions stay visible even when the
 *  filter panel is collapsed. */
export interface AppliedFilterChip {
  /** Stable unique key for React + tests, e.g. `"status:done"`. */
  key: string;
  /** Which filter dimension this chip removes from. */
  dimension:
    | 'category'
    | 'assignee'
    | 'status'
    | 'system'
    | 'area'
    | 'scenario'
    | 'phase'
    | 'search';
  /** Raw value within the dimension (the search text for `'search'`). */
  value: string;
  /** Human-facing label shown on the chip. */
  label: string;
}

/**
 * Flatten the active filter into a flat, ordered list of chips. The order
 * mirrors the filter panel's chip-group order (status → category → 担当 →
 * フェーズ → シナリオ → system → area → 検索) so the inline summary reads
 * the same way the panel does. `labels.status` / `labels.assignee` resolve
 * the stored value to a display name; without them the raw value is shown
 * (assignee ids fall back to the id, the `UNASSIGNED` sentinel to「(未割当)」).
 * The search condition renders as `q: <text>`.
 */
export function buildAppliedFilterChips(
  filter: TaskListFilter,
  labels: {
    status?: (s: ExecutionStatus) => string;
    assignee?: (id: string) => string;
  } = {},
): AppliedFilterChip[] {
  const chips: AppliedFilterChip[] = [];
  for (const s of filter.statuses) {
    chips.push({
      key: `status:${s}`,
      dimension: 'status',
      value: s,
      label: labels.status ? labels.status(s) : s,
    });
  }
  for (const c of filter.categories) {
    chips.push({ key: `category:${c}`, dimension: 'category', value: c, label: c });
  }
  for (const a of filter.assignees) {
    const label =
      a === UNASSIGNED ? '(未割当)' : labels.assignee ? labels.assignee(a) : a;
    chips.push({ key: `assignee:${a}`, dimension: 'assignee', value: a, label });
  }
  for (const p of filter.phaseLabels) {
    chips.push({ key: `phase:${p}`, dimension: 'phase', value: p, label: p });
  }
  for (const s of filter.scenarios) {
    chips.push({ key: `scenario:${s}`, dimension: 'scenario', value: s, label: s });
  }
  for (const s of filter.systems) {
    chips.push({ key: `system:${s}`, dimension: 'system', value: s, label: s });
  }
  for (const a of filter.areas) {
    chips.push({ key: `area:${a}`, dimension: 'area', value: a, label: a });
  }
  if (filter.search) {
    chips.push({
      key: 'search',
      dimension: 'search',
      value: filter.search,
      label: `q: ${filter.search}`,
    });
  }
  return chips;
}

/**
 * Return a new filter with a single applied condition removed. For the
 * array dimensions the matching `value` is dropped; for `'search'` the
 * text is cleared. The original filter is left untouched.
 */
export function removeAppliedFilter(
  filter: TaskListFilter,
  dimension: AppliedFilterChip['dimension'],
  value: string,
): TaskListFilter {
  const without = (list: readonly string[]) => list.filter((v) => v !== value);
  switch (dimension) {
    case 'category':
      return { ...filter, categories: without(filter.categories) };
    case 'assignee':
      return { ...filter, assignees: without(filter.assignees) };
    case 'status':
      return {
        ...filter,
        statuses: filter.statuses.filter((v) => v !== value),
      };
    case 'system':
      return { ...filter, systems: without(filter.systems) };
    case 'area':
      return { ...filter, areas: without(filter.areas) };
    case 'scenario':
      return { ...filter, scenarios: without(filter.scenarios) };
    case 'phase':
      return { ...filter, phaseLabels: without(filter.phaseLabels) };
    case 'search':
      return { ...filter, search: '' };
  }
}

export interface AssigneeOption {
  value: string;
  label: string;
}

export function collectAssigneeOptions(
  rows: readonly TaskListRow[],
): AssigneeOption[] {
  const map = new Map<string, string>();
  let hasUnassigned = false;
  for (const r of rows) {
    const value = r.assigneeId;
    if (value) {
      map.set(value, r.assignee?.name ?? value);
    } else {
      hasUnassigned = true;
    }
  }
  const opts: AssigneeOption[] = [...map.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label, 'ja'));
  if (hasUnassigned) opts.push({ value: UNASSIGNED, label: '(未割当)' });
  return opts;
}
